/**
 * ExportDialog Component
 * Feature: 021-infrastructure-data-collection (T059-T060)
 * Constitution: v1.12.0 (Production Integration Edition)
 *
 * Modal dialog for exporting infrastructure snapshot data.
 * Supports snapshot type selection, time range picker, and progress tracking.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Download, CheckCircle2, AlertCircle } from 'lucide-react';
import {
  Button,
  Checkbox,
  Select,
  Alert,
  Badge,
} from '../../components/ui';
import { AppModal } from '../../components/ui/AppModal';

const Divider = () => <hr className="my-4 border-[var(--color-border)]" />;

// ============================================================================
// TYPES
// ============================================================================

// Capacity type matching database values
import { CAPACITY_TYPE_OPTIONS, type CapacityType } from '../../services/snapshotsApi';
export type { CapacityType };

// Export-specific types
type SnapshotType =
  | 'capacity'
  | 'workspace'
  | 'lakehouse'
  | 'warehouse'
  | 'pipeline'
  | 'notebook'
  | 'report'
  | 'semantic_model'
  | 'storage_account';

type ExportStatus = 'queued' | 'processing' | 'completed' | 'failed';

interface ExportJob {
  export_job_id: string;
  status: ExportStatus;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  file_size_bytes?: number;
  records_exported?: Record<string, number>;
  download_url?: string;
  error_message?: string;
}

interface ExportDialogProps {
  open: boolean;
  onClose: () => void;
  initialCapacityType?: CapacityType;
  initialTimeRange?: number;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const SNAPSHOT_TYPE_OPTIONS: { value: SnapshotType; label: string }[] = [
  { value: 'capacity', label: 'Capacities' },
  { value: 'workspace', label: 'Workspaces' },
  { value: 'lakehouse', label: 'Lakehouses' },
  { value: 'warehouse', label: 'Warehouses' },
  { value: 'pipeline', label: 'Pipelines' },
  { value: 'notebook', label: 'Notebooks' },
  { value: 'report', label: 'Reports' },
  { value: 'semantic_model', label: 'Semantic Models' },
  { value: 'storage_account', label: 'Storage Accounts' },
];

const TIME_RANGE_OPTIONS = [
  { value: 7, label: 'Last 7 days' },
  { value: 30, label: 'Last 30 days' },
  { value: 90, label: 'Last 90 days' },
  { value: 180, label: 'Last 6 months' },
  { value: 365, label: 'Last year' },
];

const POLL_INTERVAL = 2000; // 2 seconds

// ============================================================================
// COMPONENT
// ============================================================================

export const ExportDialog: React.FC<ExportDialogProps> = ({
  open,
  onClose,
  initialCapacityType,
  initialTimeRange = 30,
}) => {
  // Form state
  const [selectedTypes, setSelectedTypes] = useState<SnapshotType[]>([
    'capacity',
    'workspace',
    'lakehouse',
  ]);
  const [timeRangeDays, setTimeRangeDays] = useState<number>(initialTimeRange);
  const [capacityType, setCapacityType] = useState<CapacityType | ''>(
    initialCapacityType || ''
  );

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportJob, setExportJob] = useState<ExportJob | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedTypes(['capacity', 'workspace', 'lakehouse']);
      setTimeRangeDays(initialTimeRange);
      setCapacityType(initialCapacityType || '');
      setExportJob(null);
      setError(null);
      setIsExporting(false);
    }
  }, [open, initialCapacityType, initialTimeRange]);

  // Poll export status
  useEffect(() => {
    if (!exportJob) {
      return;
    }
    if (['completed', 'failed'].includes(exportJob.status)) {
      // A finished job ends the export. Without this the form stayed locked and
      // the "Retry Export" offered for a failed job could never be clicked.
      setIsExporting(false);
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await fetch(
          `/api/infrastructure-snapshots/export/${exportJob.export_job_id}`
        );
        if (!response.ok) {
          throw new Error('Failed to get export status');
        }
        const result = await response.json();
        if (result.success) {
          setExportJob(result.data);
        }
      } catch (err) {
        console.error('Failed to poll export status:', err);
      }
    };

    const interval = setInterval(pollStatus, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [exportJob]);

  // Handle type selection
  const handleTypeChange = useCallback(
    (type: SnapshotType) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        setSelectedTypes((prev) => [...prev, type]);
      } else {
        setSelectedTypes((prev) => prev.filter((t) => t !== type));
      }
    },
    []
  );

  // Select/Deselect all types
  const handleSelectAll = useCallback(() => {
    if (selectedTypes.length === SNAPSHOT_TYPE_OPTIONS.length) {
      setSelectedTypes([]);
    } else {
      setSelectedTypes(SNAPSHOT_TYPE_OPTIONS.map((o) => o.value));
    }
  }, [selectedTypes.length]);

  // Start export
  const handleStartExport = useCallback(async () => {
    if (selectedTypes.length === 0) {
      setError('Please select at least one snapshot type');
      return;
    }

    setIsExporting(true);
    setError(null);

    try {
      const response = await fetch('/api/infrastructure-snapshots/export', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          snapshot_types: selectedTypes,
          time_range_days: timeRangeDays,
          capacity_type: capacityType || undefined,
          format: 'csv',
        }),
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || 'Failed to start export');
      }

      const result = await response.json();
      if (result.success) {
        setExportJob(result.data);
      } else {
        throw new Error(result.error || 'Failed to start export');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start export');
      setIsExporting(false);
    }
  }, [selectedTypes, timeRangeDays, capacityType]);

  // Download file
  const handleDownload = useCallback(() => {
    if (exportJob?.download_url) {
      window.location.href = exportJob.download_url;
    }
  }, [exportJob]);

  // Format file size
  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Render export progress
  const renderProgress = () => {
    if (!exportJob) return null;

    const status = exportJob.status;

    return (
      <div className="mt-4">
        <Divider />

        {/* Status indicator */}
        <div className="mb-4 flex items-center">
          {status === 'queued' && (
            <>
              <div className="mr-4 h-1.5 flex-grow overflow-hidden rounded-full bg-[var(--color-bg-subtle)]">
                <div className="h-full w-1/3 animate-pulse bg-[var(--color-accent)]" />
              </div>
              <span className="text-sm text-[var(--color-text-secondary)]">
                Queued...
              </span>
            </>
          )}
          {status === 'processing' && (
            <>
              <div className="mr-4 h-1.5 flex-grow overflow-hidden rounded-full bg-[var(--color-bg-subtle)]">
                <div className="h-full w-1/3 animate-pulse bg-[var(--color-accent)]" />
              </div>
              <span className="text-sm text-[var(--color-text-secondary)]">
                Exporting...
              </span>
            </>
          )}
          {status === 'completed' && (
            <>
              <CheckCircle2 className="mr-1 h-4 w-4 text-[var(--color-status-success)]" />
              <span className="text-sm text-[var(--color-status-success)]">
                Export completed
              </span>
            </>
          )}
          {status === 'failed' && (
            <>
              <AlertCircle className="mr-1 h-4 w-4 text-[var(--color-status-danger)]" />
              <span className="text-sm text-[var(--color-status-danger)]">
                Export failed
              </span>
            </>
          )}
        </div>

        {/* Error message */}
        {status === 'failed' && exportJob.error_message && (
          <Alert variant="error" className="mb-4">
            {exportJob.error_message}
          </Alert>
        )}

        {/* Success details */}
        {status === 'completed' && (
          <div className="mb-4">
            {/* File size */}
            {exportJob.file_size_bytes && (
              <p className="mb-1 text-sm text-[var(--color-text-secondary)]">
                File size: {formatFileSize(exportJob.file_size_bytes)}
              </p>
            )}

            {/* Records exported */}
            {exportJob.records_exported && (
              <div className="mb-1">
                <p className="mb-0.5 text-sm text-[var(--color-text-secondary)]">
                  Records exported:
                </p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(exportJob.records_exported).map(
                    ([type, count]) => (
                      <Badge key={type} variant="default" isOutlined>
                        {`${type}: ${count.toLocaleString()}`}
                      </Badge>
                    )
                  )}
                </div>
              </div>
            )}

            {/* Download button */}
            {exportJob.download_url && (
              <Button
                variant="primary"
                leftIcon={<Download className="h-4 w-4" />}
                onClick={handleDownload}
                className="mt-4"
              >
                Download Export
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      size="xl"
      title="Export Infrastructure Data"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isExporting && !exportJob}>
            {exportJob?.status === 'completed' ? 'Close' : 'Cancel'}
          </Button>
          {(!exportJob || exportJob.status === 'failed') && (
            <Button
              variant="primary"
              onClick={handleStartExport}
              disabled={isExporting || selectedTypes.length === 0}
            >
              {exportJob?.status === 'failed' ? 'Retry Export' : 'Start Export'}
            </Button>
          )}
        </>
      }
    >
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}

      {/* Snapshot Types */}
      <div className="mb-6">
        <div className="mb-1 flex items-center">
          <span className="text-sm font-medium text-[var(--color-text-primary)]">Snapshot Types</span>
          <Button variant="ghost" size="sm" className="ml-2" onClick={handleSelectAll}>
            {selectedTypes.length === SNAPSHOT_TYPE_OPTIONS.length
              ? 'Deselect All'
              : 'Select All'}
          </Button>
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {SNAPSHOT_TYPE_OPTIONS.map((option) => (
            <div key={option.value} className="min-w-[150px]">
              <Checkbox
                checked={selectedTypes.includes(option.value)}
                onChange={handleTypeChange(option.value)}
                disabled={isExporting}
                label={option.label}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Time Range */}
      <div className="mb-6">
        <Select
          isFullWidth
          label="Time Range"
          value={timeRangeDays}
          onChange={(e) => setTimeRangeDays(Number(e.target.value))}
          disabled={isExporting}
          options={TIME_RANGE_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
        />
      </div>

      {/* Capacity Type Filter (optional) */}
      <div className="mb-6">
        <Select
          isFullWidth
          label="Capacity Type Filter (Optional)"
          value={capacityType}
          onChange={(e) => setCapacityType(e.target.value as CapacityType | '')}
          disabled={isExporting}
          options={[
            { value: '', label: 'All Capacity Types' },
            ...CAPACITY_TYPE_OPTIONS,
          ]}
        />
      </div>

      {/* Export Progress */}
      {renderProgress()}
    </AppModal>
  );
};

export default ExportDialog;
